import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ScrollToTop } from './components/ScrollToTop';
import Topbar from './components/Topbar';
import { ROUTES } from './routes';

function lazyPage(loadModule) {
  let ResolvedPage;
  let loadError;
  let pendingLoad;
  const listeners = new Set();

  function preload() {
    if (ResolvedPage) return Promise.resolve();
    if (!pendingLoad) {
      pendingLoad = loadModule()
        .then((module) => {
          ResolvedPage = module.default;
        })
        .catch((error) => {
          loadError = error;
          throw error;
        })
        .finally(() => listeners.forEach((listener) => listener()));
    }
    return pendingLoad;
  }

  function LazyPage(props) {
    const [, renderLoadedPage] = useState(0);

    useEffect(() => {
      if (ResolvedPage || loadError) return undefined;

      const handleLoad = () => renderLoadedPage((count) => count + 1);
      listeners.add(handleLoad);
      preload().catch(() => {});

      return () => listeners.delete(handleLoad);
    }, []);

    if (ResolvedPage) return <ResolvedPage {...props} />;
    if (loadError) throw loadError;
    return <div />;
  }

  LazyPage.preload = preload;
  return LazyPage;
}

const Home = lazyPage(() => import('./pages/Home'));
const Safety = lazyPage(() => import('./pages/Safety'));
const SafetyVictoria = lazyPage(() => import('./pages/SafetyVictoria'));
const SafetyNSW = lazyPage(() => import('./pages/SafetyNSW'));
const SafetyQLD = lazyPage(() => import('./pages/SafetyQLD'));
const SafetySA = lazyPage(() => import('./pages/SafetySA'));
const Tradies = lazyPage(() => import('./pages/Tradies'));
const HomeMods = lazyPage(() => import('./pages/HomeMods'));
const EnergyRebates = lazyPage(() => import('./pages/EnergyRebates/ComingSoon'));
const Contact = lazyPage(() => import('./pages/Contact'));
const Careers = lazyPage(() => import('./pages/Careers'));
const CareerDetail = lazyPage(() => import('./pages/Careers/CareerDetail'));
const PrivacyPolicy = lazyPage(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazyPage(() => import('./pages/TermsAndConditions'));
const NotFound = lazyPage(() => import('./pages/NotFound'));

const normalisePath = (pathname) => pathname.replace(/\/+$/, '') || '/';

const ROUTE_PRELOADERS = new Map([
  [normalisePath(ROUTES.home), Home.preload],
  [normalisePath(ROUTES.safety), Safety.preload],
  [normalisePath(ROUTES.safetyLegacy), Safety.preload],
  ['/safety-complience', Safety.preload],
  [normalisePath(ROUTES.safetyVictoria), SafetyVictoria.preload],
  ['/safety-complience/victoria', SafetyVictoria.preload],
  [normalisePath(ROUTES.safetyNewSouthWales), SafetyNSW.preload],
  ['/safety-complience/new-south-wales', SafetyNSW.preload],
  [normalisePath(ROUTES.safetyQueensland), SafetyQLD.preload],
  ['/safety-complience/queensland', SafetyQLD.preload],
  [normalisePath(ROUTES.safetySouthAustralia), SafetySA.preload],
  ['/safety-complience/south-australia', SafetySA.preload],
  [normalisePath(ROUTES.tradies), Tradies.preload],
  [normalisePath(ROUTES.homeModifications), HomeMods.preload],
  [normalisePath(ROUTES.energyRebates), EnergyRebates.preload],
  [normalisePath(ROUTES.energyRebatesAirConditioning), EnergyRebates.preload],
  [normalisePath(ROUTES.energyRebatesHotWater), EnergyRebates.preload],
  [normalisePath(ROUTES.energyRebatesInsulation), EnergyRebates.preload],
  [normalisePath(ROUTES.energyRebatesShowerHeads), EnergyRebates.preload],
  [normalisePath(ROUTES.contact), Contact.preload],
  [normalisePath(ROUTES.careers), Careers.preload],
  [normalisePath(ROUTES.careersSmokeAlarmTechnician), CareerDetail.preload],
  [normalisePath(ROUTES.careersElectrician), CareerDetail.preload],
  [normalisePath(ROUTES.careersElectricianQueensland), CareerDetail.preload],
  [normalisePath(ROUTES.careersElectricianVictoria), CareerDetail.preload],
  [normalisePath(ROUTES.careersPlumber), CareerDetail.preload],
  [normalisePath(ROUTES.privacyPolicy), PrivacyPolicy.preload],
  [normalisePath(ROUTES.termsAndConditions), TermsAndConditions.preload],
]);

export function preloadRoute(pathname) {
  return (ROUTE_PRELOADERS.get(normalisePath(pathname)) || NotFound.preload)();
}

export default function App() {
  return (
    <>
      <Topbar />

      <ScrollToTop>
        <Routes>
          <Route path={ROUTES.home} element={<Home />} />
          <Route path={ROUTES.safety} element={<Safety />} />
          <Route path={ROUTES.safetyVictoria} element={<SafetyVictoria />} />
          <Route path={ROUTES.safetyNewSouthWales} element={<SafetyNSW />} />
          <Route path={ROUTES.safetyQueensland} element={<SafetyQLD />} />
          <Route path={ROUTES.safetySouthAustralia} element={<SafetySA />} />

          <Route path={ROUTES.safetyLegacy} element={<Navigate to={ROUTES.safety} replace />} />
          <Route path="/safety-complience" element={<Navigate to={ROUTES.safety} replace />} />
          <Route path="/safety-complience/victoria" element={<Navigate to={ROUTES.safetyVictoria} replace />} />
          <Route path="/safety-complience/new-south-wales" element={<Navigate to={ROUTES.safetyNewSouthWales} replace />} />
          <Route path="/safety-complience/queensland" element={<Navigate to={ROUTES.safetyQueensland} replace />} />
          <Route path="/safety-complience/south-australia" element={<Navigate to={ROUTES.safetySouthAustralia} replace />} />

          <Route path={ROUTES.tradies} element={<Tradies />} />
          <Route path={ROUTES.homeModifications} element={<HomeMods />} />
          <Route path={ROUTES.energyRebates} element={<EnergyRebates />} />
          <Route path={ROUTES.energyRebatesAirConditioning} element={<Navigate to={ROUTES.energyRebates} replace />} />
          <Route path={ROUTES.energyRebatesHotWater} element={<Navigate to={ROUTES.energyRebates} replace />} />
          <Route path={ROUTES.energyRebatesInsulation} element={<Navigate to={ROUTES.energyRebates} replace />} />
          <Route path={ROUTES.energyRebatesShowerHeads} element={<Navigate to={ROUTES.energyRebates} replace />} />
          <Route path={ROUTES.contact} element={<Contact />} />
          <Route path={ROUTES.careers} element={<Careers />} />
          <Route path={ROUTES.careersSmokeAlarmTechnician} element={<CareerDetail />} />
          <Route path={ROUTES.careersElectrician} element={<Navigate to={ROUTES.careersElectricianVictoria} replace />} />
          <Route path={ROUTES.careersElectricianQueensland} element={<CareerDetail />} />
          <Route path={ROUTES.careersElectricianVictoria} element={<CareerDetail />} />
          <Route path={ROUTES.careersPlumber} element={<CareerDetail />} />
          <Route path={ROUTES.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={ROUTES.termsAndConditions} element={<TermsAndConditions />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ScrollToTop>
    </>
  );
}
